import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import useEmblaCarousel from 'embla-carousel-react';
import noop from 'lodash/noop';

import { ArrowButton } from 'components/common/buttonsComponents';
import ThumbnailButton from 'components/common/ThumbnailButton/ThumbnailButton';
import { sliderWidth, slideWidth, SliderWrapper } from './ThumbnailSlider.styles';
import { NO_IMAGE_PLACEHOLDER } from '../../../constants/images';

const slidesOnLine = Math.floor(sliderWidth / slideWidth);

const ThumbnailSlider = ({
  imageObjects = [],
  selectedIndex = 0,
  setSelectedIndex = noop,
  maxSlidesOnLine = slidesOnLine,
}) => {
  const isArrowsVisible = imageObjects.length > maxSlidesOnLine;

  const [thumbViewportRef, emblaThumbs] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    selectedClass: '',
    draggable: isArrowsVisible,
  });

  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(true);

  const scrollToPrev = useCallback(() => {
    if (emblaThumbs && prevBtnEnabled) {
      const prevIndex = selectedIndex - 1;
      setSelectedIndex(prevIndex);
    }
  }, [emblaThumbs, prevBtnEnabled, selectedIndex, setSelectedIndex]);

  const scrollToNext = useCallback(() => {
    if (emblaThumbs && nextBtnEnabled) {
      const nextIndex = selectedIndex + 1;
      setSelectedIndex(nextIndex);
    }
  }, [emblaThumbs, nextBtnEnabled, selectedIndex, setSelectedIndex]);

  const onThumbClick = useCallback(
    (index) => {
      if (emblaThumbs) {
        setSelectedIndex(index);
      }
    },
    [emblaThumbs, setSelectedIndex]
  );

  const onSelect = useCallback(() => {
    if (emblaThumbs) {
      setPrevBtnEnabled(emblaThumbs.canScrollPrev());
      setNextBtnEnabled(emblaThumbs.canScrollNext());
    }
  }, [emblaThumbs]);

  useEffect(() => {
    if (emblaThumbs) {
      onSelect();
      emblaThumbs.on('select', onSelect);
    }
  }, [emblaThumbs, onSelect]);

  useEffect(() => {
    if (emblaThumbs) {
      emblaThumbs.scrollTo(selectedIndex);
    }
  }, [emblaThumbs, selectedIndex]);

  return (
    <SliderWrapper className={isArrowsVisible ? 'has-arrows' : ''}>
      <div className="embla">
        <div className="embla__viewport" ref={thumbViewportRef}>
          <div className={`embla__container ${!isArrowsVisible ? 'centered' : ''}`}>
            {imageObjects.map((imageObj, index) => (
              <div className={'embla__slide'} key={imageObj.fileName + index}>
                <ThumbnailButton
                  imgSrc={imageObj.src ?? NO_IMAGE_PLACEHOLDER}
                  altText={imageObj.fileName}
                  isSelected={index === selectedIndex}
                  className={'zoom-in'}
                  onThumbClick={() => {
                    onThumbClick(index);
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {isArrowsVisible && (
        <>
          <ArrowButton
            type={'button'}
            className={'arrow arrow-left'}
            onClick={scrollToPrev}
            disabled={!prevBtnEnabled}
            aria-label={'prev'}
          />
          <ArrowButton
            type={'button'}
            className={'arrow arrow-right'}
            onClick={scrollToNext}
            disabled={!nextBtnEnabled}
            aria-label={'next'}
          />
        </>
      )}
    </SliderWrapper>
  );
};

export const ImageObjectType = PropTypes.shape({
  fileName: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
});

ThumbnailSlider.propTypes = {
  imageObjects: PropTypes.arrayOf(ImageObjectType).isRequired,
  selectedIndex: PropTypes.number.isRequired,
  setSelectedIndex: PropTypes.func.isRequired,
  maxSlidesOnLine: PropTypes.number,
};

export default ThumbnailSlider;
